import { COMMAND_OVERRIDE_LIMIT, type CommandOverride, type CommandOverrideTarget } from '@spawnwire/shared';

/** The editing logic behind the "Who can use commands" screen, free of React so it can be tested on its own. */

/** What the screen shows for a role or person: no rule, always allowed, or never allowed. */
export type RuleState = 'default' | 'allow' | 'deny';

export function ruleState(overrides: readonly CommandOverride[], type: CommandOverrideTarget, id: string): RuleState {
  const found = overrides.find((o) => o.targetType === type && o.targetId === id);
  return found ? (found.allow ? 'allow' : 'deny') : 'default';
}

/** The rules with one role's or person's changed. `default` removes the rule. Channels can only be switched off. */
export function withRule(overrides: readonly CommandOverride[], type: CommandOverrideTarget, id: string, state: RuleState): CommandOverride[] {
  const rest = overrides.filter((o) => !(o.targetType === type && o.targetId === id));
  if (state === 'default') return rest;
  if (type === 'channel') return state === 'deny' ? [...rest, { targetType: type, targetId: id, allow: false }] : rest;
  return [...rest, { targetType: type, targetId: id, allow: state === 'allow' }];
}

/** Whether a command is switched off in a channel. */
export const isOffIn = (overrides: readonly CommandOverride[], channelId: string): boolean => ruleState(overrides, 'channel', channelId) === 'deny';

/** Two sets of rules say the same thing, whatever order they are in. */
export function sameRules(a: readonly CommandOverride[], b: readonly CommandOverride[]): boolean {
  const key = (o: CommandOverride) => `${o.targetType}:${o.targetId}:${o.allow ? 1 : 0}`;
  const ka = a.map(key).sort();
  const kb = b.map(key).sort();
  return ka.length === kb.length && ka.every((k, i) => k === kb[i]);
}

/** Whether another rule can be added. */
export const hasRoom = (overrides: readonly CommandOverride[]): boolean => overrides.length < COMMAND_OVERRIDE_LIMIT;

/** "No rules", "1 rule", "3 rules": how a command's rules are summed up in the list. */
export function ruleSummary(count: number): string {
  return count === 0 ? 'Default' : count === 1 ? '1 rule' : `${count} rules`;
}
